import { Form, notification } from 'antd'
import i18next from 'i18next'
import { observer } from 'mobx-react-lite'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { ArrowLeftOutlined } from '@ant-design/icons'

import { useStore } from 'store/useStore'
import { SignboardFormat, SignboardSize } from 'sections/LKSection/modules/OrderModule/types'
import { ContainerLK } from 'sections/LKSection/shared/components/ContainerLK/ContainerLK'
import { signboardPhotoItems } from 'sections/LKSection/shared/PickPointSharedModule/components/PickPointFilesForm/data'
import { PickPointFileItem } from 'sections/LKSection/shared/PickPointSharedModule/components/PickPointFilesForm/PickPointFileItem/PickPointFileItem'
import { signboardFormatOptions, signboardSizeOptions } from 'sections/LKSection/shared/PickPointSharedModule/data'
import { Margin, Padding } from 'components/ui'
import { Select, Skeleton, Title } from 'components/uiKit'
import { Button } from 'components/uiKit/Button/Button'
import { Flex } from 'components/uiKit/Flex/Flex'
import { useAsyncEffect } from 'utils/hooks/useAsyncEffect'
import { LKRoutesEnum } from 'shared/constants/routes'
import { SEARCH_PARAMS } from 'shared/constants/values'

import { PvzKeys } from '../../data'
import { UpdateSignboardData } from '../../types/typesDTO'

const getKey = (key: string) => `${PvzKeys.signboardPage}.${key}`
const getApiKey = (key: string) => `${PvzKeys.pvzModule}.api.${key}`

export const PvzSignboardPage = observer(() => {
  const { viewPvzUiStore: uiStore } = useStore()
  const { pvzId } = useParams()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const [isLoading, setIsLoading] = useState(true)
  const [isFormChanged, setIsFormChanged] = useState(false)
  const location = useLocation()

  const searchParams = new URLSearchParams(location.search)
  const pageFromUrl = Number(searchParams.get(SEARCH_PARAMS.page)) || 1
  const pageSizeFromUrl = Number(searchParams.get(SEARCH_PARAMS.pageSize)) || 20

  const pvz = uiStore.selectedPvz

  const backButtonHandler = () => {
    navigate(LKRoutesEnum.pvz)
  }

  const handleValuesChange = () => {
    const touched = form.isFieldsTouched()
    setIsFormChanged(touched)
  }

  useAsyncEffect(async () => {
    setIsLoading(true)
    uiStore.setPage(pageFromUrl)
    uiStore.setPageSize(pageSizeFromUrl)

    if (uiStore.pvzList.length === 0) {
      await uiStore.getPvzList()
    }

    if (pvzId) {
      await uiStore.switchPvz(+pvzId)
    }

    await uiStore.getPvzSignboardList(Number(uiStore.selectedPvz?.externalId))
    const signboardData = uiStore.signboardData
    form.setFieldsValue({
      signboardFormat: signboardData.signboardFormat,
      signboardSize: signboardData.signboardSize,
    })

    setIsLoading(false)
  }, [pvzId, form, uiStore])

  const updateSignboardData = async (values: { signboardFormat: SignboardFormat; signboardSize: SignboardSize }) => {
    const patch: UpdateSignboardData = {
      pickpointId: Number(uiStore.selectedPvz?.externalId),
      signboardFormat: values.signboardFormat,
      signboardSize: values.signboardSize,
    }
    await uiStore.updatePvzSignboard(patch)
    await uiStore.getPvzSignboardList(Number(uiStore.selectedPvz?.externalId))
    notification.success({
      message: i18next.t(getApiKey('updatePvzSignboardSuccess')),
    })
  }

  const handleUpdateSignboard = async (values: { signboardFormat: SignboardFormat; signboardSize: SignboardSize }) => {
    if (!pvzId) return

    if (values.signboardFormat === SignboardFormat.none || values.signboardSize === SignboardSize.none) {
      notification.error({
        message: t(getKey('Пожалуйста, заполните поля')),
      })
      return
    }

    const hasSignboardChanges = isFormChanged
    const hasPhotoChanges = !uiStore.isNoChanges

    if (hasSignboardChanges && !hasPhotoChanges) {
      await updateSignboardData(values)
    } else if (!hasSignboardChanges && hasPhotoChanges) {
      await uiStore.updatePvz(Number(pvzId))
    } else if (hasSignboardChanges && hasPhotoChanges) {
      await updateSignboardData(values)
      await uiStore.updatePvz(Number(pvzId))
    }

    const signboardData = uiStore.signboardData
    form.setFieldsValue({
      signboardFormat: signboardData.signboardFormat || SignboardFormat.none,
      signboardSize: signboardData.signboardSize || SignboardSize.none,
    })

    navigate(LKRoutesEnum.pvz)
  }

  return (
    <ContainerLK>
      <Padding py={32} style={{ width: '100%' }}>
        <Margin mb={32}>
          <Flex gap={16} align="center">
            <Button shape="circle" icon={<ArrowLeftOutlined />} onClick={backButtonHandler} />

            <Title>{t(getKey('Вывеска'))}</Title>
          </Flex>
        </Margin>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleUpdateSignboard}
          onValuesChange={handleValuesChange}
          style={{ maxWidth: '650px' }}
        >
          <Skeleton loading={isLoading} active paragraph={{ rows: 1 }}>
            <Form.Item
              name="signboardFormat"
              label={t(getKey('Какая вывеска сейчас на фасаде'))}
              getValueProps={(value) => ({
                value: value === SignboardFormat.none ? undefined : value,
              })}
              rules={[
                {
                  required: true,
                  message: t(getKey('Укажите формат вывески')),
                },
              ]}
            >
              <Select
                options={signboardFormatOptions.map((item) => ({
                  value: item.value,
                  label: t(getKey(item.label)),
                }))}
                onChange={(value) => form.setFieldsValue({ signboardFormat: value })}
                placeholder={
                  form.getFieldValue('signboardFormat') === SignboardFormat.none
                    ? t(getKey('Не установлено'))
                    : t(getKey('Выберите формат вывески'))
                }
              />
            </Form.Item>
          </Skeleton>
          <Skeleton loading={isLoading} active paragraph={{ rows: 1 }}>
            <Form.Item
              name="signboardSize"
              label={t(getKey('На фасаде возможно разместить'))}
              getValueProps={(value) => ({
                value: value === SignboardSize.none ? undefined : value,
              })}
              rules={[
                {
                  required: true,
                  message: t(getKey('Укажите размер вывески')),
                },
              ]}
            >
              <Select
                options={signboardSizeOptions.map((item, index) => ({
                  value: item.value,
                  label: index === 0 ? t(getKey(item.label)) : item.label,
                }))}
                onChange={(value) => form.setFieldsValue({ signboardSize: value })}
                placeholder={
                  form.getFieldValue('signboardFormat') === SignboardFormat.none
                    ? t(getKey('Не установлено'))
                    : t(getKey('Выберите формат вывески'))
                }
              />
            </Form.Item>
          </Skeleton>

          <Form.Item label={t(getKey('Загрузка файлов'))}>
            <Flex vertical gap={16}>
              {signboardPhotoItems.map((photoItem) => (
                <Skeleton key={photoItem.fileType} active paragraph={{ rows: 2 }} loading={isLoading}>
                  <Form.Item key={photoItem.fileType}>
                    <PickPointFileItem
                      uiStore={uiStore.fileList}
                      entity={pvz || null}
                      fileType={photoItem.fileType}
                      label={photoItem.label}
                      description={photoItem.description}
                      dataTestId={photoItem.dataTestId}
                      initialized={uiStore.initialized}
                    />
                  </Form.Item>
                </Skeleton>
              ))}
            </Flex>
          </Form.Item>
          <Margin mt={16}>
            <Flex>
              <Button
                htmlType="submit"
                disabled={
                  (uiStore.isNoChanges && !isFormChanged) ||
                  form.getFieldsError().some((field) => field.errors.length > 0)
                }
              >
                {t(getKey('Обновить данные'))}
              </Button>
            </Flex>
          </Margin>
        </Form>
      </Padding>
    </ContainerLK>
  )
})
